import { environment } from '../../../environments/environment';
import type { Environment } from '../tokens/app.tokens';

/**
 * Il file che il deployment scrive accanto all'artefatto pubblicato, letto prima che l'applicazione parta.
 */
export const RUNTIME_CONFIG_FILE = 'config.json';

/**
 * Cio' che il deployment puo' dire, e nient'altro: dove sta il BFF. Le proprieta' arrivano da un file che
 * il codice non compila, quindi si dichiarano `unknown` e si validano una per una — un numero al posto di
 * un indirizzo e' un errore di chi configura, non un tipo da dare per buono.
 */
interface RuntimeConfig {
  readonly apiUrl?: unknown;
  readonly hubUrl?: unknown;
}

/**
 * L'ambiente con cui l'applicazione parte: i valori compilati, con l'indirizzo del BFF che il deployment
 * puo' sostituire senza ricompilare niente.
 *
 * @remarks
 * L'indirizzo del BFF e' l'unica cosa che cambia da un portale all'altro a parita' di artefatto: la stessa
 * build serve piu' domini e piu' ambienti, e inciderlo nel bundle vorrebbe dire un pacchetto diverso per
 * ciascuno — e una ricompilazione per spostare un host. Dichiararlo qui lo rende un fatto del deployment,
 * ed e' anche cio' che permette di tornare ai percorsi relativi — SPA e BFF dietro lo stesso host, che
 * resta la forma per cui il portale e' disegnato — svuotando un campo invece di rifare la build.
 *
 * Si legge col `fetch` del browser e non con `HttpClient` perche' qui l'iniezione non esiste ancora:
 * l'indirizzo serve a costruire `ENVIRONMENT`, che e' proprio cio' da cui gli altri lo leggono.
 *
 * Un file assente, illeggibile o malformato **non** ferma l'avvio: valgono i valori compilati, che sono i
 * percorsi relativi. Un portale che non parte non dice a nessuno cosa manca; un portale che parte e non
 * raggiunge il BFF lo dice alla prima chiamata, col suo indirizzo scritto nell'errore.
 */
export async function loadRuntimeEnvironment(
  fetchFile: typeof fetch = (input, init) => fetch(input, init),
  baseUrl: string = document.baseURI,
): Promise<Environment> {
  const config = await readConfig(fetchFile, baseUrl);

  return {
    ...environment,
    apiUrl: declaredOr(config.apiUrl, environment.apiUrl),
    hubUrl: declaredOr(config.hubUrl, environment.hubUrl),
  };
}

/**
 * Il file si chiede accanto alla radice del portale, non accanto alla rotta aperta: `config.json` da
 * `/onboarding/language` sarebbe `/onboarding/config.json`, che non esiste. E si chiede senza cache,
 * perche' l'indirizzo del BFF e' il valore che cambia quando si sposta un ambiente: una copia vecchia
 * manderebbe la SPA a parlare col posto sbagliato.
 */
async function readConfig(fetchFile: typeof fetch, baseUrl: string): Promise<RuntimeConfig> {
  try {
    const response = await fetchFile(new URL(RUNTIME_CONFIG_FILE, baseUrl), { cache: 'no-store' });
    if (!response.ok) return {};

    const content: unknown = await response.json();

    return isObject(content) ? content : {};
  } catch {
    return {};
  }
}

/**
 * Un indirizzo dichiarato vale solo se e' un indirizzo: una stringa vuota o fatta di spazi e' un campo
 * lasciato li' dal deployment, e vale come non dichiarato. Lo slash finale si toglie perche' chi legge
 * questo valore ci appende la propria rotta, che con lo slash suo comincia.
 *
 * Un portale pubblicato che chiama se' stesso — `/api/v1/...` che torna l'`index.html` — ha quasi sempre
 * questo campo vuoto: e' il `config.json` di `src/config/production`, vuoto per scelta, copiato da una
 * build `production` finita nell'ambiente sbagliato. Prima del codice si guarda con quale configurazione
 * e' stata costruita la build pubblicata.
 */
function declaredOr(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;

  const declared = value.trim().replace(/\/+$/, '');

  return declared === '' ? fallback : declared;
}

function isObject(value: unknown): value is RuntimeConfig {
  return typeof value === 'object' && value !== null;
}
